// Consent-gated analytics banner.
//
// Public-health, public-good posture: analytics are opt-in only. This banner
// appears on the first visit (once the visitor has no stored choice) and lets
// them Accept or Reject. The choice persists in localStorage:
//   Accept → store 'granted', load GA4 (gtag) with anonymize_ip
//   Reject → store 'denied', load nothing
// On return visits the stored choice is applied silently in app boot (see
// initAnalyticsFromStoredConsent in lib/analytics.ts), so this banner only
// renders when no choice exists yet.
//
// Styling matches the app's design tokens (see index.css) and is deliberately
// small and unobtrusive — a bottom strip, not a blocking modal.

import { useEffect, useState } from 'react';
import { Link } from 'wouter';
import { Cookie } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getConsent, setConsent, loadAnalytics } from '../lib/analytics';

export function CookieConsent() {
  const { t } = useTranslation();
  // Default hidden so no-JS / SSR never flashes the banner. We reveal it in
  // useEffect only when localStorage confirms there's no prior choice.
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (getConsent() === null) setVisible(true);
  }, []);

  if (!visible) return null;

  function accept() {
    setConsent('granted');
    loadAnalytics();
    setVisible(false);
  }

  function reject() {
    setConsent('denied');
    setVisible(false);
  }

  return (
    <div
      role="region"
      aria-label={t('cookie_consent.aria')}
      className="fixed inset-x-0 bottom-0 z-[90] px-3 pb-3 sm:px-4 sm:pb-4"
    >
      <div className="mx-auto flex max-w-3xl flex-col gap-3 rounded-2xl border border-[var(--color-line-strong)] bg-[var(--color-surface)]/95 px-4 py-3.5 shadow-[0_12px_32px_-8px_rgba(0,0,0,0.35)] backdrop-blur sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:px-5">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[var(--color-accent-soft)] text-[var(--color-accent)]">
          <Cookie size={15} strokeWidth={2.25} />
        </span>
        <p className="text-[12.5px] leading-relaxed text-[var(--color-text-2)]">
          {t('cookie_consent.body')}{' '}
          <Link
            href="/disclaimer"
            className="font-medium text-[var(--color-accent)] underline-offset-2 hover:underline"
          >
            {t('cookie_consent.learn_more')}
          </Link>
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-2 self-end sm:self-auto">
        <button
          type="button"
          onClick={reject}
          className="inline-flex h-9 items-center justify-center rounded-lg px-4 text-[12.5px] font-medium text-[var(--color-text-2)] ring-1 ring-[var(--color-line-strong)] transition-colors hover:bg-[var(--color-surface-2)]"
        >
          {t('cookie_consent.reject')}
        </button>
        <button
          type="button"
          onClick={accept}
          className="inline-flex h-9 items-center justify-center rounded-lg bg-[var(--color-accent)] px-4 text-[12.5px] font-semibold text-white shadow-[0_2px_4px_rgba(82,71,229,0.25)] transition-colors hover:bg-[var(--color-accent-hover)]"
        >
          {t('cookie_consent.accept')}
        </button>
      </div>
      </div>
    </div>
  );
}
